import { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { BackIcon, SearchIcon, Close } from 'app/components/icons'
import QuestionItem from 'app/components/QuestionItem'
import { setHelpTabState, setHelpTabInputFocus } from 'app/redux/widget/widget.actions'
import useDelayUnmount from 'app/hooks/useDelayUnmount'
import QuestionDetail from 'app/components/QuestionDetail'
import FAQList from './components/FAQList'
import FAQDetail from './components/FAQDetail'
import CloseWidgetBtn from 'app/components/CloseWidgetBtn'
import { HELP_TAB_STATE } from 'app/constants/launcherContentStates'
import './help.scss'

const getFaqs = ({ widgetData }) => widgetData.widgetInfo.faq
const getHelpTabState = ({ widgetData }) => widgetData.widgetInfo.helpTabState
const getInputFocus = ({ widgetData }) => widgetData.widgetInfo.helpTabInputFocus
const getLanguageInfo = ({ widgetData }) => widgetData.widgetInfo.languageInfo
const getIsWidgetWideOpen = ({ widgetData }) => widgetData.widgetInfo.isWidgetWideOpen
const getSelectedQuestion = ({ messagesData }) => messagesData.selectedQuestion

//? "ct" stands for current tab
const Help = ({ ct }) => {
  const faqs = useSelector(getFaqs)
  const helpTabState = useSelector(getHelpTabState)
  const inputFocus = useSelector(getInputFocus)
  const languageInfo = useSelector(getLanguageInfo)
  const isWidgetWideOpen = useSelector(getIsWidgetWideOpen)
  const selectedQuestion = useSelector(getSelectedQuestion)

  const [searchInputValue, setSearchInputValue] = useState('')
  const [debouncedSearchedText, setDebouncedSearchedText] = useState('')
  const inputRef = useRef(null)
  const dispatch = useDispatch()
  const [isMounted, setIsMounted] = useState(ct === 2)
  const shouldRenderChild = useDelayUnmount(isMounted, 200)

  const handleSetTabState = (state) => {
    dispatch(setHelpTabState(state))
  }

  const getSearchedItemsLength = () => {
    let count = 0

    faqs.forEach((f) => {
      const length = f.questions.filter((q) =>
        q.question.toUpperCase().includes(debouncedSearchedText.toUpperCase()),
      ).length

      count += length
    })

    return count
  }

  const handleReturnBackToList = () => {
    // setState("FAQ_LIST");
    handleSetTabState(HELP_TAB_STATE.FAQ_LIST)
    setSearchInputValue('')
  }

  const handleChangeSearchInput = (e) => {
    setSearchInputValue(e.target.value)
  }

  const renderSearchBoxIcon =
    debouncedSearchedText !== '' ? (
      <div
        className="help_tab_header_search_box_icon_close"
        onClick={() => {
          setSearchInputValue('')
          setDebouncedSearchedText('')
        }}
      >
        <Close />
      </div>
    ) : (
      <div onClick={() => inputRef.current?.focus()} className="help_tab_header_search_box_icon_search">
        <SearchIcon />
      </div>
    )

  useEffect(() => {
    if (inputFocus) {
      inputRef.current?.focus()
    }

    return () => {
      dispatch(setHelpTabInputFocus(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputFocus])

  useEffect(() => {
    if (ct !== 2) {
      setIsMounted(false)
    } else {
      setIsMounted(true)
    }
  }, [ct])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchedText(searchInputValue), 500)

    return () => {
      clearTimeout(timer)
    }
  }, [searchInputValue])

  if (!shouldRenderChild) return null

  if (isWidgetWideOpen && !!selectedQuestion) {
    return <QuestionDetail />
  }

  return (
    <div className={`${isMounted ? 'component_enter' : 'component_exit'} help_tab_container`}>
      <div className={`help_tab `}>
        <div className="help_tab_header">
          <div className="help_tab_header_title">
            <div>
              <CloseWidgetBtn />
            </div>
            <div className="help_tab_header_title_content">
              <span className="help_tab_header_title_content__title">{languageInfo.texts.knowledgeBase}</span>
            </div>
            <div onClick={handleReturnBackToList} className="help_tab_header_title_icon_container">
              <div className="help_tab_header_title_icon_container_flex">
                {helpTabState === 'FAQ_DETAIL' && (
                  <div className="help_tab_header_title_back_icon">
                    <BackIcon color={'#fff'} />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="help_tab_header_search_box">
            <input
              value={searchInputValue}
              onChange={handleChangeSearchInput}
              placeholder={languageInfo.texts.search}
              ref={inputRef}
              onBlur={() => {
                dispatch(setHelpTabInputFocus(false))
              }}
            />
            {renderSearchBoxIcon}
          </div>
        </div>
        <div className="help_tab_body">
          {/* Show when searching */}
          {debouncedSearchedText !== '' && (
            <div className="help_tab_body_faq_question_list">
              {faqs.map((faq) => {
                return faq.questions?.map((q, i) => {
                  if (q.question.toUpperCase().includes(debouncedSearchedText.toUpperCase()))
                    return (
                      <div key={i} className="help_tab_body_faq_question_list_item_container">
                        <QuestionItem question={q} />
                      </div>
                    )
                  return null
                })
              })}
              <div className="help_tab_body_faq_question_list_not_found">
                {getSearchedItemsLength() === 0 && languageInfo.texts.questionNotFound}
              </div>
            </div>
          )}

          {/* render collections/faqs */}
          {helpTabState === HELP_TAB_STATE.FAQ_LIST && debouncedSearchedText === '' && <FAQList />}

          {/* render collection/faq detail */}
          {helpTabState === HELP_TAB_STATE.FAQ_DETAIL && debouncedSearchedText === '' && <FAQDetail />}
        </div>
      </div>
    </div>
  )
}

export default Help
